import React, { useState, useEffect, useCallback } from 'react';
import {
  upsellService,
  UpsellOpportunity,
  UpsellCreatePayload,
  UpsellConvertPayload,
  UpsellStage,
} from '../services/upsellService';
import { crmService, Client } from '../services/crmService';
import { XMarkIcon } from '../components/Icons';
import { ListPageHeader } from '../components/crm/ListPageHeader';

const STAGES: { key: UpsellStage; label: string }[] = [
  { key: 'identified', label: 'Identified' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'proposal_discussed', label: 'Proposal Discussed' },
  { key: 'closed_won', label: 'Closed Won' },
  { key: 'closed_lost', label: 'Closed Lost' },
];

const formatCurrency = (val: string) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

const UpsellPipeline: React.FC = () => {
  const [upsells, setUpsells] = useState<UpsellOpportunity[]>([]);
  const [contracts, setContracts] = useState<{ id: string; lead_email: string; lead_name: string }[]>([]);
  const [crmClients, setCrmClients] = useState<Client[]>([]);
  const [legacyLeads, setLegacyLeads] = useState<
    { id: string; email: string; first_name: string; last_name: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedUpsell, setSelectedUpsell] = useState<UpsellOpportunity | null>(null);

  const loadUpsells = useCallback(async () => {
    try {
      setLoading(true);
      const res = await upsellService.getUpsells({ page: 1 });
      setUpsells(res.results || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load upsells');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadContractsAndLeads = useCallback(async () => {
    try {
      const [contractsRes, clients, legacy] = await Promise.all([
        upsellService.getContracts(),
        crmService.getAllClients({ stage: 'lead' }),
        upsellService.getAllLeads(),
      ]);
      setContracts(contractsRes.results || []);
      setCrmClients(clients);
      setLegacyLeads(legacy);
    } catch {
      setContracts([]);
      setCrmClients([]);
      setLegacyLeads([]);
    }
  }, []);

  useEffect(() => {
    loadUpsells();
  }, [loadUpsells]);

  useEffect(() => {
    if (showCreateModal) loadContractsAndLeads();
  }, [showCreateModal, loadContractsAndLeads]);

  const handleCreate = async (data: UpsellCreatePayload) => {
    try {
      await upsellService.createUpsell(data);
      await loadUpsells();
      setShowCreateModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create upsell');
    }
  };

  const handleUpdate = async (id: string, data: Partial<UpsellOpportunity>) => {
    try {
      await upsellService.updateUpsell(id, data);
      await loadUpsells();
      setShowEditModal(false);
      setSelectedUpsell(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update upsell');
    }
  };

  const handleMoveStage = async (upsell: UpsellOpportunity, newStage: UpsellStage) => {
    try {
      await upsellService.updateUpsell(upsell.id, { stage: newStage });
      await loadUpsells();
      if (newStage === 'closed_won' && !upsell.converted_contract) {
        setSelectedUpsell({ ...upsell, stage: newStage });
        setShowConvertModal(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move stage');
    }
  };

  const handleConvert = async (id: string, payload: UpsellConvertPayload) => {
    try {
      await upsellService.convertUpsell(id, payload);
      await loadUpsells();
      setShowConvertModal(false);
      setSelectedUpsell(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this upsell opportunity?')) return;
    try {
      await upsellService.deleteUpsell(id);
      await loadUpsells();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const byStage = STAGES.reduce((acc, { key }) => {
    acc[key] = upsells.filter((u) => u.stage === key);
    return acc;
  }, {} as Record<UpsellStage, UpsellOpportunity[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ListPageHeader
        title="Upsell Pipeline"
        actions={
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="btn-primary px-4 py-2 rounded-xl text-sm font-medium"
          >
            Create upsell
          </button>
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-4">
          {STAGES.map(({ key, label }) => (
            <div
              key={key}
              className="w-64 flex-shrink-0 bg-gray-50 dark:bg-gray-800 rounded-lg border border-border p-3"
            >
              <div className="font-medium text-fg mb-3 text-sm">{label}</div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                {byStage[key]?.map((u) => (
                  <UpsellCard
                    key={u.id}
                    upsell={u}
                    onEdit={() => {
                      setSelectedUpsell(u);
                      setShowEditModal(true);
                    }}
                    onMoveStage={(stage) => handleMoveStage(u, stage)}
                    onConvert={() => {
                      setSelectedUpsell(u);
                      setShowConvertModal(true);
                    }}
                    onDelete={() => handleDelete(u.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showCreateModal && (
        <CreateUpsellModal
          contracts={contracts}
          crmClients={crmClients}
          legacyLeads={legacyLeads}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
        />
      )}
      {showEditModal && selectedUpsell && (
        <EditUpsellModal
          upsell={selectedUpsell}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUpsell(null);
          }}
          onSubmit={(data) => handleUpdate(selectedUpsell.id, data)}
        />
      )}
      {showConvertModal && selectedUpsell && (
        <ConvertUpsellModal
          upsell={selectedUpsell}
          onClose={() => {
            setShowConvertModal(false);
            setSelectedUpsell(null);
          }}
          onSubmit={(payload) => handleConvert(selectedUpsell.id, payload)}
        />
      )}
    </div>
  );
};

function UpsellCard({
  upsell,
  onEdit,
  onMoveStage,
  onConvert,
  onDelete,
}: {
  upsell: UpsellOpportunity;
  onEdit: () => void;
  onMoveStage: (stage: UpsellStage) => void;
  onConvert: () => void;
  onDelete: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const idx = STAGES.findIndex((s) => s.key === upsell.stage);
  const prevStage = idx > 0 ? STAGES[idx - 1].key : null;
  const nextStage = idx >= 0 && idx < STAGES.length - 1 ? STAGES[idx + 1].key : null;

  return (
    <div className="bg-input rounded-lg border border-gray-200 dark:border-gray-600 p-3 shadow-sm relative group">
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-fg truncate">{upsell.name}</div>
          <div className="text-sm text-primary dark:text-purple-400 mt-0.5">
            {formatCurrency(upsell.potential_value)}
          </div>
          {upsell.lead_name && (
            <div className="text-xs text-fg-muted truncate mt-1">
              {upsell.lead_name}
            </div>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 mt-1 w-36 bg-surface rounded shadow-lg border border-border z-20 py-1">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    onEdit();
                  }}
                  className="block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted"
                >
                  Edit
                </button>
                {upsell.stage === 'closed_won' && !upsell.converted_contract && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onConvert();
                    }}
                    className="block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted"
                  >
                    Convert to Contract
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowMenu(false);
                    onDelete();
                  }}
                  className="block w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-1 mt-2">
        {prevStage && (
          <button
            onClick={() => onMoveStage(prevStage)}
            className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-500"
          >
            &larr;
          </button>
        )}
        {nextStage && (
          <button
            onClick={() => onMoveStage(nextStage)}
            className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-500 ml-auto"
          >
            &rarr;
          </button>
        )}
      </div>
    </div>
  );
}

function CreateUpsellModal({
  contracts,
  crmClients,
  legacyLeads,
  onClose,
  onSubmit,
}: {
  contracts: { id: string; lead_email: string; lead_name: string }[];
  crmClients: Client[];
  legacyLeads: { id: string; email: string; first_name: string; last_name: string }[];
  onClose: () => void;
  onSubmit: (data: UpsellCreatePayload) => void;
}) {
  const [sourceType, setSourceType] = useState<'contract' | 'contact'>('contract');
  const [contractId, setContractId] = useState('');
  /** `c:<clientUuid>` for CRM pipeline lead, `l:<leadUuid>` for legacy Lead rows */
  const [contactKey, setContactKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [potentialValue, setPotentialValue] = useState('');
  const [stage, setStage] = useState<UpsellStage>('identified');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: UpsellCreatePayload = {
      name,
      description: description || undefined,
      potential_value: potentialValue,
      stage,
      expected_close_date: expectedCloseDate || undefined,
    };
    if (sourceType === 'contract') {
      payload.contract_id = contractId || undefined;
    } else {
      const sep = contactKey.indexOf(':');
      const kind = sep >= 0 ? contactKey.slice(0, sep) : '';
      const id = sep >= 0 ? contactKey.slice(sep + 1) : '';
      if (kind === 'c' && id) payload.client_id = id;
      else if (kind === 'l' && id) payload.lead_id = id;
    }
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-fg">Create Upsell</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon size={5} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">
                Tied to
              </label>
              <div className="flex gap-4 mb-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={sourceType === 'contract'}
                    onChange={() => setSourceType('contract')}
                    className="mr-2"
                  />
                  Contract
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={sourceType === 'contact'}
                    onChange={() => setSourceType('contact')}
                    className="mr-2"
                  />
                  CRM / contact
                </label>
              </div>
              {sourceType === 'contract' ? (
                <>
                  <select
                    value={contractId}
                    onChange={(e) => setContractId(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                    required
                  >
                    <option value="">Select contract</option>
                    {contracts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.lead_name} - {c.lead_email}
                      </option>
                    ))}
                  </select>
                  {contracts.length === 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">No contracts found. Create contracts first.</p>
                  )}
                </>
              ) : (
                <>
                  <select
                    value={contactKey}
                    onChange={(e) => setContactKey(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                    required
                  >
                    <option value="">Select pipeline lead or contact</option>
                    {crmClients.length > 0 && (
                      <optgroup label="CRM pipeline (Leads)">
                        {crmClients.map((c) => (
                          <option key={`c:${c.id}`} value={`c:${c.id}`}>
                            {c.full_name} — {c.email}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {legacyLeads.length > 0 && (
                      <optgroup label="Marketing / form leads">
                        {legacyLeads.map((l) => (
                          <option key={`l:${l.id}`} value={`l:${l.id}`}>
                            {l.first_name} {l.last_name} — {l.email}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {crmClients.length === 0 && legacyLeads.length === 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      No contacts found. Add leads on the Leads page or capture form leads.
                    </p>
                  )}
                </>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">
                Potential Value ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={potentialValue}
                onChange={(e) => setPotentialValue(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">
                Stage
              </label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value as UpsellStage)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
              >
                {STAGES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">
                Expected Close Date
              </label>
              <input
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="flex-1 bg-primary hover:bg-primary-dark text-white py-2 rounded-lg font-medium"
              >
                Create
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-border rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function EditUpsellModal({
  upsell,
  onClose,
  onSubmit,
}: {
  upsell: UpsellOpportunity;
  onClose: () => void;
  onSubmit: (data: Partial<UpsellOpportunity>) => void;
}) {
  const [name, setName] = useState(upsell.name);
  const [description, setDescription] = useState(upsell.description || '');
  const [potentialValue, setPotentialValue] = useState(upsell.potential_value);
  const [stage, setStage] = useState<UpsellStage>(upsell.stage);
  const [expectedCloseDate, setExpectedCloseDate] = useState(upsell.expected_close_date || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, description: description || null, potential_value: potentialValue, stage, expected_close_date: expectedCloseDate || null });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-fg">Edit Upsell</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon size={5} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Potential Value ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={potentialValue}
                onChange={(e) => setPotentialValue(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Stage</label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value as UpsellStage)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
              >
                {STAGES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Expected Close Date</label>
              <input
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 bg-primary hover:bg-primary-dark text-white py-2 rounded-lg font-medium">
                Save
              </button>
              <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-lg">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ConvertUpsellModal({
  upsell,
  onClose,
  onSubmit,
}: {
  upsell: UpsellOpportunity;
  onClose: () => void;
  onSubmit: (payload: UpsellConvertPayload) => void;
}) {
  const [contractType, setContractType] = useState<'one_time' | 'recurring'>('one_time');
  const [recurringInterval, setRecurringInterval] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      contract_type: contractType,
      recurring_interval: contractType === 'recurring' ? recurringInterval : undefined,
      start_date: startDate,
      end_date: endDate || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-fg">Convert to Contract</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon size={5} />
            </button>
          </div>
          <p className="text-sm text-fg-secondary mb-4">
            Convert &quot;{upsell.name}&quot; ({formatCurrency(upsell.potential_value)}) to a new contract?
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Contract Type</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input type="radio" checked={contractType === 'one_time'} onChange={() => setContractType('one_time')} className="mr-2" />
                  One-Time
                </label>
                <label className="flex items-center">
                  <input type="radio" checked={contractType === 'recurring'} onChange={() => setContractType('recurring')} className="mr-2" />
                  Recurring
                </label>
              </div>
            </div>
            {contractType === 'recurring' && (
              <div>
                <label className="block text-sm font-medium text-fg-secondary mb-1">Interval</label>
                <select
                  value={recurringInterval}
                  onChange={(e) => setRecurringInterval(e.target.value as any)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-secondary mb-1">End Date (optional)</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium">
                Convert
              </button>
              <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-lg">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default UpsellPipeline;
